import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Wand2, Edit, Save, X, Loader2, Check, BookOpen, HandHelping, Star, Eye, PlusCircle, MinusCircle, Equal } from 'lucide-react';
import { API, norm } from '@/lib/helpers';

export default function EscalasModal({ open, onClose, eventId, eventStatus, pessoas = [], allowedFunctionIds }) {
  const [tab, setTab] = useState('recitativo');
  const [functions, setFunctions] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [excludedRotationFids, setExcludedRotationFids] = useState([]);
  const [replacementOverrides, setReplacementOverrides] = useState({});
  const [funcReplacements, setFuncReplacements] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyingPreview, setApplyingPreview] = useState(false);

  const pessoaMap = useMemo(() => {
    const m = {};
    for (const p of pessoas) m[p.pessoa_id] = p;
    return m;
  }, [pessoas]);

  const allAuxiliares = useMemo(() => pessoas.filter(p => p.auxiliar_jovens), [pessoas]);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/service-functions`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/service-schedules/${eventId}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/attendance/event/${eventId}`, { credentials: 'include' }).then(r => r.ok ? r.json() : [])
    ]).then(([funcs, sched, attData]) => {
      const activeFuncs = funcs.filter(f => f.active && !f.deleted);
      setFunctions(activeFuncs);
      setSchedule(sched);
      setExcludedRotationFids(sched?.excluded_rotation_fids || []);
      const attMap = {};
      const repOverrides = {}; // Build replacement overrides from attendance replacement_log
      const funcRepsMap = {}; // { function_id: [{ absentPid, absentName, replacementPid, replacementName }] }
      for (const a of attData) {
        attMap[a.pessoa_id] = { presente: a.presente };
        if (!a.presente && a.replacement_log) {
          const absentP = pessoas.find(p => p.pessoa_id === a.pessoa_id);
          const absentName = absentP ? (absentP.apelido || (absentP.nome_completo || '').split(' ')[0]) : '';
          for (const rep of a.replacement_log) {
            // Match functions - by function_id if available, or by name for ALL matches
            const matchedFuncs = rep.function_id
              ? activeFuncs.filter(f => f.function_id === rep.function_id)
              : activeFuncs.filter(f => f.nome === rep.function_name);
            for (const func of matchedFuncs) {
              // Recitativo overrides (swap absent with replacement in schedule)
              if (func.tipo === 'recitativo') {
                if (!repOverrides[func.function_id]) {
                  repOverrides[func.function_id] = { removed: [], added: [] };
                }
                repOverrides[func.function_id].removed.push(a.pessoa_id);
                if (rep.replacement_id) repOverrides[func.function_id].added.push(rep.replacement_id);
              }
              // Function replacements map (used by BOTH tabs for yellow badges)
              if (!funcRepsMap[func.function_id]) funcRepsMap[func.function_id] = [];
              funcRepsMap[func.function_id].push({
                absentPid: a.pessoa_id,
                absentName,
                replacementPid: rep.replacement_id || null,
                replacementName: rep.replacement_name || '',
              });
            }
          }
        }
      }
      setAttendance(attMap);
      setReplacementOverrides(repOverrides);
      setFuncReplacements(funcRepsMap);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { if (open && eventId) fetchData(); }, [open, eventId]);

  const recFuncs = functions.filter(f => (f.tipo || 'outros') === 'recitativo');
  const outrosFuncs = functions.filter(f => {
    if ((f.tipo || 'outros') === 'recitativo') return false;
    if (allowedFunctionIds?.length > 0 && !allowedFunctionIds.includes(f.function_id)) return false;
    if (excludedRotationFids.includes(f.function_id)) return false;
    return true;
  });

  // Schedule overrides for recitativo
  const scheduleOverrides = {};
  if (schedule?.assignments) {
    for (const a of schedule.assignments) {
      const fid = a.function_id;
      if (recFuncs.some(f => f.function_id === fid)) {
        scheduleOverrides[fid] = [...(a.male || []), ...(a.female || [])];
      }
    }
  }
  // Apply replacement_log overrides for recitativos without schedule entries
  for (const fid of Object.keys(replacementOverrides)) {
    if (!scheduleOverrides[fid]) {
      const func = recFuncs.find(f => f.function_id === fid);
      if (func) {
        let list = [...(func.assigned_people || [])];
        const rep = replacementOverrides[fid];
        for (const removedPid of rep.removed) {
          list = list.filter(pid => pid !== removedPid);
        }
        for (const addedPid of rep.added) {
          if (!list.includes(addedPid)) list.push(addedPid);
        }
        scheduleOverrides[fid] = list;
      }
    }
  }
  // Reconstruct original basePeople: add back absent originals and remove their replacements
  // so that replacement badges render correctly
  for (const fid of Object.keys(funcReplacements)) {
    if (!scheduleOverrides[fid]) continue;
    const reps = funcReplacements[fid];
    for (const r of reps) {
      // Add back the absent person if not already in the list
      if (r.absentPid && !scheduleOverrides[fid].includes(r.absentPid)) {
        scheduleOverrides[fid].push(r.absentPid);
      }
      // Remove the replacement person (they'll show via the replacement badge)
      if (r.replacementPid && scheduleOverrides[fid].includes(r.replacementPid)) {
        // Only remove if this replacement isn't also an original assignee elsewhere
        const isOriginalAssignee = reps.some(rep => rep.absentPid === r.replacementPid);
        if (!isOriginalAssignee) {
          scheduleOverrides[fid] = scheduleOverrides[fid].filter(pid => pid !== r.replacementPid);
        }
      }
    }
  }

  // Stats
  const totalAux = allAuxiliares.length;
  const presentAux = allAuxiliares.filter(p => attendance[p.pessoa_id]?.presente).length;
  const absentAux = allAuxiliares.filter(p => attendance[p.pessoa_id] && !attendance[p.pessoa_id].presente).length;

  const filteredRecFuncs = recFuncs;

  const startEdit = () => {
    const e = {};
    const funcsToEdit = tab === 'recitativo' ? filteredRecFuncs : outrosFuncs;
    for (const f of funcsToEdit) {
      if (tab === 'recitativo') {
        e[f.function_id] = [...(scheduleOverrides[f.function_id] || f.assigned_people || [])];
      } else {
        const a = schedule?.assignments?.find(a => a.function_id === f.function_id);
        e[f.function_id] = a ? [...(a.male || []), ...(a.female || [])] : [];
      }
    }
    setEdits(e);
    setEditing(true);
  };

  const togglePerson = (fid, pid) => {
    setEdits(prev => {
      const current = prev[fid] || [];
      const next = current.includes(pid) ? current.filter(id => id !== pid) : [...current, pid];
      return { ...prev, [fid]: next };
    });
  };

  // Which functions a person is already assigned to (for duplicate indicator)
  const personAssignments = useMemo(() => {
    const map = {};
    const funcsToCheck = tab === 'recitativo' ? filteredRecFuncs : outrosFuncs;
    const source = editing ? edits : null;
    for (const f of funcsToCheck) {
      const pids = source ? (source[f.function_id] || []) : (tab === 'recitativo' ? (scheduleOverrides[f.function_id] || f.assigned_people || []) : (() => { const a = schedule?.assignments?.find(a => a.function_id === f.function_id); return a ? [...(a.male || []), ...(a.female || [])] : []; })());
      for (const pid of pids) {
        if (!map[pid]) map[pid] = [];
        map[pid].push(f.nome);
      }
    }
    return map;
  }, [editing, edits, filteredRecFuncs, outrosFuncs, tab, scheduleOverrides, schedule]);

  const saveEdits = async () => {
    setSaving(true);
    try {
      let sched = schedule;
      if (!sched) {
        const r = await fetch(`${API}/service-schedules`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ event_id: eventId, assignments: [] })
        });
        sched = await r.json();
      }
      const existingAssignments = [...(sched.assignments || [])];
      for (const [fid, pids] of Object.entries(edits)) {
        const males = pids.filter(pid => pessoaMap[pid]?.genero?.toLowerCase() === 'masculino');
        const females = pids.filter(pid => pessoaMap[pid]?.genero?.toLowerCase() !== 'masculino');
        const func = functions.find(f => f.function_id === fid);
        const idx = existingAssignments.findIndex(a => a.function_id === fid);
        const entry = { function_id: fid, function_name: func?.nome || '', male: males, female: females, locked: tab === 'recitativo' };
        if (idx >= 0) existingAssignments[idx] = entry;
        else existingAssignments.push(entry);
      }
      await fetch(`${API}/service-schedules/${eventId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ assignments: existingAssignments })
      });
      toast.success('Escala atualizada!');
      setEditing(false);
      fetchData();
    } catch { toast.error('Erro ao salvar'); }
    setSaving(false);
  };

  const handleReanalyze = async () => {
    setSuggesting(true);
    try {
      const r = await fetch(`${API}/service-schedules/${eventId}/suggest`, { method: 'POST', credentials: 'include' });
      if (!r.ok) throw new Error('Erro');
      const data = await r.json();
      const newAssignments = data.assignments || [];
      setPreview(buildPreview(newAssignments));
    } catch { toast.error('Erro ao reanalisar critérios'); }
    setSuggesting(false);
  };

  const applyPreview = async () => {
    if (!preview?.assignments) return;
    setApplyingPreview(true);
    try {
      await fetch(`${API}/service-schedules/${eventId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ assignments: preview.assignments, status: 'confirmada' })
      });
      setSchedule(prev => ({ ...(prev || { event_id: eventId }), assignments: preview.assignments, status: 'confirmada' }));
      setPreview(null);
      toast.success('Escala reanalisada e aplicada!');
      fetchData();
    } catch { toast.error('Erro ao aplicar reanálise'); }
    setApplyingPreview(false);
  };

  const buildPreview = (newAssignments) => {
    const oldAssignments = schedule?.assignments || [];
    const relevantFuncs = functions.filter(f => {
      if (!f.active || f.deleted) return false;
      if (allowedFunctionIds?.length > 0 && !allowedFunctionIds.includes(f.function_id)) return false;
      if (excludedRotationFids.includes(f.function_id)) return false;
      return true;
    });
    const rows = relevantFuncs.map(func => {
      const currentAssignment = oldAssignments.find(a => a.function_id === func.function_id);
      const suggestedAssignment = newAssignments.find(a => a.function_id === func.function_id);
      const current = [...new Set(currentAssignment ? [...(currentAssignment.male || []), ...(currentAssignment.female || [])] : ((func.tipo === 'recitativo') ? (scheduleOverrides[func.function_id] || func.assigned_people || []) : []))];
      const suggested = [...new Set(suggestedAssignment ? [...(suggestedAssignment.male || []), ...(suggestedAssignment.female || [])] : [])];
      const currentSet = new Set(current);
      const suggestedSet = new Set(suggested);
      const kept = suggested.filter(pid => currentSet.has(pid));
      const added = suggested.filter(pid => !currentSet.has(pid));
      const removed = current.filter(pid => !suggestedSet.has(pid));
      return { function_id: func.function_id, function_name: func.nome, tipo: func.tipo || 'outros', current, suggested, kept, added, removed };
    });
    return { assignments: newAssignments, rows };
  };

  // ---- Shared edit row (used by both tabs in edit mode) ----
  const renderEditRow = (func, i, isRec) => {
    const assigned = [...new Set(edits[func.function_id] || [])];
    const total = (func.qty_male || 0) + (func.qty_female || 0);
    const gender = func.eligibility?.gender || 'todos';
    const eligible = allAuxiliares.filter(p => {
      const g = (p.genero || '').toLowerCase();
      if (gender === 'masculino' && g !== 'masculino') return false;
      if (gender === 'feminino' && g !== 'feminino') return false;
      if (!isRec) {
        const filtros = func.eligibility?.filtros_pessoa || [];
        for (const filtro of filtros) {
          if (filtro === 'apenas_auxiliares') { if (!p.auxiliar_jovens) return false; }
          else { if (!p[filtro]) return false; }
        }
      }
      return true;
    });
    return (
      <div key={func.function_id} className={`px-3 py-2 ${i > 0 ? 'border-t border-slate-100' : ''}`}>
        <div className="flex flex-wrap items-center gap-1">
          <p className="text-sm font-semibold text-slate-800 mr-1">{func.nome}</p>
          {eligible.map(p => {
            const selected = assigned.includes(p.pessoa_id);
            const nome = p.apelido || norm(p.nome_completo || '').split(' ')[0];
            const att = attendance[p.pessoa_id];
            const dotColor = att ? (att.presente ? 'bg-green-500' : 'bg-red-400') : 'bg-slate-300';
            const otherFuncs = (personAssignments[p.pessoa_id] || []).filter(n => n !== func.nome);
            return (
              <TooltipProvider key={p.pessoa_id} delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" onClick={() => togglePerson(func.function_id, p.pessoa_id)}
                      className={`inline-flex items-center gap-1 px-1.5 h-5 rounded text-[10px] border transition-colors ${selected ? 'bg-blue-100 border-blue-400 text-blue-800 font-medium' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                      {nome}
                    </button>
                  </TooltipTrigger>
                  {otherFuncs.length > 0 && <TooltipContent><p className="text-xs">Já em: {otherFuncs.join(', ')}</p></TooltipContent>}
                </Tooltip>
              </TooltipProvider>
            );
          })}
          <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${assigned.length >= total ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{assigned.length}/{total}</span>
        </div>
      </div>
    );
  };

  // ---- Helper to render a normal person badge ----
  const renderNormalBadge = (pid, func) => {
    const p = pessoaMap[pid];
    const nome = p ? (p.apelido || norm(p.nome_completo || '').split(' ')[0]) : pid;
    const att = attendance[pid];
    const dotColor = att ? (att.presente ? 'bg-green-500' : 'bg-red-400') : 'bg-slate-300';
    return (
      <span key={pid} className="inline-flex items-center gap-1 px-1.5 h-5 rounded text-[10px] border bg-slate-50 border-slate-200 text-slate-600"
        data-testid={`badge-${func.function_id}-${pid}`}>
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
        {nome}
      </span>
    );
  };

  // ---- Helper to render a combined replacement badge: 🔴 Ausente → Substituto ----
  const renderReplacementBadge = (absentPid, replacementPid, func) => {
    const absentP = pessoaMap[absentPid];
    const absentName = absentP ? (absentP.apelido || norm(absentP.nome_completo || '').split(' ')[0]) : absentPid;
    const repP = pessoaMap[replacementPid];
    const repName = repP ? (repP.apelido || norm(repP.nome_completo || '').split(' ')[0]) : replacementPid;
    const tooltipText = `${absentName} marcou ausência, ${repName} substitui`;
    const badgeEl = (
      <span key={`rep-${absentPid}-${replacementPid}`}
        className="inline-flex items-center gap-1 px-1.5 h-5 rounded text-[10px] border bg-amber-50 border-amber-300 text-amber-700"
        data-testid={`badge-rep-${func.function_id}-${absentPid}`}>
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-red-400" />
        {absentName} <span className="text-amber-400">→</span> {repName}
      </span>
    );
    return (
      <TooltipProvider key={`rep-${absentPid}-${replacementPid}`} delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{badgeEl}</TooltipTrigger>
          <TooltipContent><p className="text-xs">{tooltipText}</p></TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // ---- Helper to render an absent-only badge (no replacement found) ----
  const renderAbsentBadge = (absentPid, func) => {
    const p = pessoaMap[absentPid];
    const nome = p ? (p.apelido || norm(p.nome_completo || '').split(' ')[0]) : absentPid;
    const tooltipText = `${nome} marcou ausência — sem substituto disponível`;
    const badgeEl = (
      <span key={`abs-${absentPid}`}
        className="inline-flex items-center gap-1 px-1.5 h-5 rounded text-[10px] border bg-amber-50 border-amber-300 text-amber-700"
        data-testid={`badge-abs-${func.function_id}-${absentPid}`}>
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-red-400" />
        {nome}
      </span>
    );
    return (
      <TooltipProvider key={`abs-${absentPid}`} delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{badgeEl}</TooltipTrigger>
          <TooltipContent><p className="text-xs">{tooltipText}</p></TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // ---- Helper: build replacement chain info for a function ----
  const buildChainInfo = (funcId) => {
    const reps = funcReplacements[funcId] || [];
    const nextMap = {}; // absentPid → replacementPid
    const prevMap = {}; // replacementPid → absentPid
    const absentSet = new Set();
    for (const r of reps) {
      absentSet.add(r.absentPid);
      if (r.replacementPid) {
        nextMap[r.absentPid] = r.replacementPid;
        prevMap[r.replacementPid] = r.absentPid;
      }
    }
    // Follow chain forward: find the person actually serving
    const getFinalReplacement = (pid) => {
      let current = nextMap[pid];
      const visited = new Set([pid]);
      while (current && absentSet.has(current) && nextMap[current] && !visited.has(current)) {
        visited.add(current);
        current = nextMap[current];
      }
      return current || null;
    };
    // Follow chain backward: find the original absent person
    const getRootAbsent = (pid) => {
      let current = prevMap[pid];
      const visited = new Set([pid]);
      while (current && prevMap[current] && !visited.has(current)) {
        visited.add(current);
        current = prevMap[current];
      }
      return current || null;
    };
    const absentNoRep = reps.filter(r => !r.replacementPid);
    return { absentSet, nextMap, prevMap, getFinalReplacement, getRootAbsent, absentNoRep };
  };

  // ---- RECITATIVOS: view row ----
  // Source: func.assigned_people (fixed rotation)
  // Follows cascading chains: Arthur → Kauã → Rafa becomes "Arthur → Rafa"
  const renderRecitativoRow = (func, i) => {
    if (editing) return renderEditRow(func, i, true);

    const basePeople = [...new Set(scheduleOverrides[func.function_id] || func.assigned_people || [])];
    const { absentSet, getFinalReplacement, absentNoRep } = buildChainInfo(func.function_id);
    const total = (func.qty_male || 0) + (func.qty_female || 0);
    const rootAbsentWithRep = basePeople.filter(pid => absentSet.has(pid) && getFinalReplacement(pid));
    const rootAbsentNoRep = basePeople.filter(pid => absentSet.has(pid) && !getFinalReplacement(pid));
    const servingCount = basePeople.length - rootAbsentWithRep.length - rootAbsentNoRep.length + rootAbsentWithRep.length;
    const leaderPid = (schedule?.leadership_map || {})[func.function_id];

    return (
      <div key={func.function_id} className={`px-3 py-2 ${i > 0 ? 'border-t border-slate-100' : ''}`}>
        <div className="flex flex-wrap items-center gap-1">
          <p className="text-sm font-semibold text-slate-800 mr-1">{func.nome}</p>
          {basePeople.length === 0 ? (
            <span className="text-[10px] text-slate-400 italic">Sem atribuição</span>
          ) : (
            basePeople.map(pid => {
              const finalRep = absentSet.has(pid) ? getFinalReplacement(pid) : null;
              // Leader check: either this person IS the leader, or leadership was transferred to their replacement
              const isLeader = leaderPid === pid || (finalRep && leaderPid === finalRep);
              const leaderName = (() => { const pp = pessoas.find(x => x.pessoa_id === pid); return pp ? (pp.apelido || (pp.nome_completo || '').split(' ')[0]) : ''; })();
              const leaderStar = isLeader ? (
                <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild>
                  <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-400 flex items-center justify-center ring-1 ring-white"><Star className="w-2 h-2 text-white fill-white" /></span>
                </TooltipTrigger><TooltipContent className="max-w-xs"><p className="text-xs"><strong>{leaderName}</strong> é o principal responsável por liderar a fila e escrever os recitativos neste evento.</p></TooltipContent></Tooltip></TooltipProvider>
              ) : null;
              if (absentSet.has(pid)) {
                const badge = finalRep ? renderReplacementBadge(pid, finalRep, func) : renderAbsentBadge(pid, func);
                return isLeader ? <span key={pid} className="relative">{badge}{leaderStar}</span> : badge;
              }
              const badge = renderNormalBadge(pid, func);
              return isLeader ? <span key={pid} className="relative">{badge}{leaderStar}</span> : badge;
            })
          )}
          <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${servingCount >= total ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{servingCount}/{total}</span>
        </div>
      </div>
    );
  };

  // ---- OUTROS: view row ----
  // Source: schedule.assignments (dynamic — absent removed, replacement already in)
  // Follows chains backward to find original absent: shows "Arthur → Rafa"
  const renderOutrosRow = (func, i) => {
    if (editing) return renderEditRow(func, i, false);

    const a = schedule?.assignments?.find(sa => sa.function_id === func.function_id);
    const schedulePids = [...new Set(a ? [...(a.male || []), ...(a.female || [])] : [])];
    const { getRootAbsent, absentNoRep } = buildChainInfo(func.function_id);
    const total = (func.qty_male || 0) + (func.qty_female || 0);
    const servingCount = schedulePids.length;

    return (
      <div key={func.function_id} className={`px-3 py-2 ${i > 0 ? 'border-t border-slate-100' : ''}`}>
        <div className="flex flex-wrap items-center gap-1">
          <p className="text-sm font-semibold text-slate-800 mr-1">{func.nome}</p>
          {schedulePids.length === 0 && absentNoRep.length === 0 ? (
            <span className="text-[10px] text-slate-400 italic">Sem atribuição</span>
          ) : (
            <>
              {schedulePids.map(pid => {
                const rootAbsent = getRootAbsent(pid);
                if (rootAbsent) {
                  return renderReplacementBadge(rootAbsent, pid, func);
                }
                return renderNormalBadge(pid, func);
              })}
              {absentNoRep.map(r => renderAbsentBadge(r.absentPid, func))}
            </>
          )}
          <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${servingCount >= total ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{servingCount}/{total}</span>
        </div>
      </div>
    );
  };

  const renderFuncs = () => {
    const isRec = tab === 'recitativo';
    const funcsToShow = isRec ? filteredRecFuncs : outrosFuncs;
    if (funcsToShow.length === 0) return <p className="text-sm text-slate-400 text-center py-4">Nenhuma função encontrada.</p>;

    // Group by gender for both tabs
    const femininas = funcsToShow.filter(f => f.eligibility?.gender === 'feminino');
    const masculinas = funcsToShow.filter(f => f.eligibility?.gender === 'masculino');
    const todos = funcsToShow.filter(f => f.eligibility?.gender !== 'feminino' && f.eligibility?.gender !== 'masculino');
    const groups = [
      ...(femininas.length > 0 ? [{ label: 'Meninas', funcs: femininas }] : []),
      ...(masculinas.length > 0 ? [{ label: 'Meninos', funcs: masculinas }] : []),
      ...(todos.length > 0 ? [{ label: 'Todos', funcs: todos }] : []),
    ];
    const rowRenderer = isRec ? renderRecitativoRow : renderOutrosRow;
    return groups.map((group, gi) => (
      <div key={group.label} className={`border border-slate-200 rounded-lg bg-white overflow-hidden ${gi > 0 ? 'mt-3' : ''}`}>
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 pt-2.5 pb-1">{group.label}</p>
        {group.funcs.map((f, i) => rowRenderer(f, i))}
      </div>
    ));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setEditing(false); onClose(); } }}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 sm:p-0 gap-0">
        {/* Fixed header */}
        <div className="px-5 pt-5 pb-3 border-b border-slate-100 space-y-3">
          <DialogHeader className="p-0 space-y-0">
            <div className="flex items-center gap-2 flex-wrap">
              <DialogTitle className="text-base">Escalas do Evento</DialogTitle>
              <Badge className="bg-green-100 text-green-700 text-[10px] font-medium">{presentAux}/{totalAux} presentes</Badge>
              {absentAux > 0 && <Badge className="bg-red-100 text-red-700 text-[10px] font-medium">{absentAux} ausentes</Badge>}
            </div>
            <DialogDescription className="sr-only">Gestão de escalas do evento</DialogDescription>
          </DialogHeader>

          {/* Tab selector + actions */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1.5" data-testid="escalas-tab-selector">
              {[{ key: 'recitativo', label: 'Recitativos', icon: BookOpen }, { key: 'outros', label: 'Outros', icon: HandHelping }].map(t => (
                <button key={t.key} onClick={() => { setTab(t.key); setEditing(false); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${tab === t.key ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                  <t.icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              {!editing && (
                <Button size="sm" variant="outline" onClick={handleReanalyze} disabled={suggesting} className="h-7 text-[11px]" data-testid="reanalyze-schedule-btn">
                  <Wand2 className={`w-3 h-3 mr-1 ${suggesting ? 'animate-spin' : ''}`} />Reanalisar critérios
                </Button>
              )}
              {!editing ? (
                <Button size="sm" variant="outline" onClick={startEdit} className="h-7 text-[11px]">
                  <Edit className="w-3 h-3 mr-1" />Editar
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-[11px] text-slate-500">
                    <X className="w-3 h-3 mr-1" />Cancelar
                  </Button>
                  <Button size="sm" onClick={saveEdits} disabled={saving} className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700">
                    <Save className="w-3 h-3 mr-1" />{saving ? 'Salvando...' : 'Salvar'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Carregando...</div>
          ) : renderFuncs()}
        </div>
      </DialogContent>
      <Dialog open={!!preview} onOpenChange={(open) => { if (!open && !applyingPreview) setPreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0" data-testid="schedule-preview-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2"><div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><Eye className="w-4 h-4" /></div><div><DialogTitle className="text-base">Prévia da reanálise</DialogTitle><DialogDescription>Confira como a escala ficará antes de aplicar.</DialogDescription></div></div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="schedule-preview-list">
            {(preview?.rows || []).map(row => {
              const hasChanges = row.added.length || row.removed.length;
              return <div key={row.function_id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`schedule-preview-row-${row.function_id}`}>
                <div className="flex items-center justify-between gap-2"><p className="text-sm font-semibold text-slate-900">{row.function_name}</p><Badge className={hasChanges ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}>{hasChanges ? 'Com alterações' : 'Sem mudanças'}</Badge></div>
                <PreviewPeople title="Permanece" icon={Equal} people={row.kept} pessoaMap={pessoaMap} tone="slate" />
                <PreviewPeople title="Adiciona" icon={PlusCircle} people={row.added} pessoaMap={pessoaMap} tone="green" />
                <PreviewPeople title="Remove" icon={MinusCircle} people={row.removed} pessoaMap={pessoaMap} tone="red" />
              </div>;
            })}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3">
            <Button variant="outline" onClick={() => setPreview(null)} disabled={applyingPreview} data-testid="schedule-preview-cancel-btn">Cancelar</Button>
            <Button onClick={applyPreview} disabled={applyingPreview} className="bg-blue-600 hover:bg-blue-700" data-testid="schedule-preview-apply-btn">{applyingPreview ? 'Aplicando...' : 'Aplicar alterações'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function PreviewPeople({ title, icon: Icon, people, pessoaMap, tone }) {
  if (!people?.length) return null;
  const toneClass = tone === 'green' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : tone === 'red' ? 'bg-red-50 text-red-700 border-red-100' : 'bg-slate-50 text-slate-600 border-slate-100';
  return <div className="mt-2"><p className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><Icon className="w-3 h-3" />{title}</p><div className="flex flex-wrap gap-1">{people.map(pid => { const p = pessoaMap[pid]; const nome = p ? (p.apelido || norm(p.nome_completo || '').split(' ')[0]) : pid; return <span key={pid} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${toneClass}`}>{nome}</span>; })}</div></div>;
}
